import type { Locale } from "@veycue/shared";
import { en } from "./en";
import { pl } from "./pl";
import type {
  Dictionary,
  PluralForms,
  TranslationKey,
  TranslationParams,
  TranslationValue,
} from "./types";

const DICTIONARIES: Record<Locale, Dictionary> = { pl, en };

export type Translate = (key: TranslationKey, params?: TranslationParams) => string;

export interface Translator {
  readonly locale: Locale;
  readonly t: Translate;
  formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string;
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  formatMoney(amount: number, currency: string): string;
  /** Względny czas („za 12 dni") — kluczowy dla komunikatów o terminach. */
  formatRelativeDays(days: number): string;
}

export function createTranslator(locale: Locale, timezone = "Europe/Warsaw"): Translator {
  const dictionary = DICTIONARIES[locale];
  const pluralRules = new Intl.PluralRules(locale);

  const t: Translate = (key, params) => {
    const entry: TranslationValue | undefined = dictionary[key];
    if (entry === undefined) {
      // Brak klucza nie może wywalić ekranu — pokazujemy sam klucz i zostawiamy ślad w logu.
      console.warn(`[i18n] brak tłumaczenia: ${String(key)} (${locale})`);
      return String(key);
    }

    const template = typeof entry === "string" ? entry : selectPlural(entry, params, pluralRules);
    return interpolate(template, params);
  };

  return {
    locale,
    t,
    formatDate: (date, options) =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeZone: timezone,
        ...options,
      }).format(date),
    formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    formatMoney: (amount, currency) =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(amount),
    formatRelativeDays: (days) =>
      new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(days, "day"),
  };
}

/**
 * Wybór formy liczby mnogiej. Polski ma trzy formy zależne od liczby (1 sprawa,
 * 2 sprawy, 5 spraw) — bez tego ekran Today generowałby komunikaty wyglądające
 * na zepsute, a to jest pierwszy ekran, jaki użytkownik widzi codziennie.
 */
function selectPlural(
  forms: PluralForms,
  params: TranslationParams | undefined,
  rules: Intl.PluralRules,
): string {
  const count = params?.count;
  if (typeof count !== "number") {
    console.warn("[i18n] klucz z liczbą mnogą użyty bez parametru `count`");
    return forms.other;
  }

  const category = rules.select(count);
  if (category === "one") return forms.one;
  if (category === "few") return forms.few ?? forms.other;
  if (category === "many") return forms.many ?? forms.other;
  return forms.other;
}

const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(template: string, params: TranslationParams | undefined): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name];
    // Brakujący parametr zostawiamy w postaci `{name}` — widoczny błąd jest lepszy
    // niż zdanie z dziurą, którego nikt nie zauważy w testach.
    return value === undefined ? match : String(value);
  });
}

export { pl, en, DICTIONARIES };
